/* Phase 2.2D — Administrative role management.
   Controlled assignment / revocation of who_guide, provider_user and
   administrator roles. Server enforces: no self-modification, provider
   binding for provider_user, last-administrator protection, full audit. */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, Search, X, Plus, ShieldAlert } from "lucide-react";
import { AdminGate, AdminShell, Field, styles } from "@/pages/AdminWorkspace";
import { useUserAuth } from "@/context/UserAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABEL = {
  veteran: "Veteran",
  family: "Family",
  who_guide: "WHO Guide",
  provider_user: "Provider user",
  administrator: "Administrator",
};

const MANAGED_ROLES = ["who_guide", "provider_user", "administrator"];

export default function AdminUsers() {
  const { user: me, authHeaders } = useUserAuth();
  const [users, setUsers] = useState(null);
  const [providers, setProviders] = useState([]);
  const [q, setQ] = useState("");
  const [assigning, setAssigning] = useState(null);
  const [status, setStatus] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/admin/users`, { headers: authHeaders(), params: q ? { q } : {} });
    setUsers(r.data.users || []);
  }, [authHeaders, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/admin/directory/providers`, { headers: authHeaders(), params: { limit: 500 } })
      .then((r) => setProviders(r.data.providers || []));
  }, [authHeaders]);

  const flash = (m) => { setStatus(m); setErr(""); setTimeout(() => setStatus(""), 3000); };
  const fail = (m) => { setErr(m); setTimeout(() => setErr(""), 5000); };

  const assign = async (userId, role, providerId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/roles`, { role, provider_id: providerId || undefined }, { headers: authHeaders() });
      setAssigning(null);
      flash(`Assigned ${ROLE_LABEL[role]}.`);
      load();
    } catch (ex) {
      throw ex;
    }
  };

  const revoke = async (u, assignment) => {
    if (!window.confirm(`Revoke ${ROLE_LABEL[assignment.role]} from ${u.email}?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}/roles/${assignment.role}`, {
        headers: authHeaders(),
        params: assignment.provider_id ? { provider_id: assignment.provider_id } : {},
      });
      flash(`Revoked ${ROLE_LABEL[assignment.role]}.`);
      load();
    } catch (ex) {
      fail(ex?.response?.data?.detail || "Revocation blocked.");
    }
  };

  return (
    <AdminGate>
      <AdminShell title="Users & Roles" testid="admin-users-page">
        <p className="mb-5 text-sm text-slateSoft max-w-3xl">
          Assign or revoke privileged roles. Provider users must be bound to an organization. You cannot modify
          your own roles, and the last administrator cannot be removed.
        </p>
        {status && <p className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2" data-testid="users-flash">{status}</p>}
        {err && <p className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="users-error">{err}</p>}

        <div className="mb-5 max-w-sm">
          <div className="relative">
            <Search className="w-4 h-4 absolute top-3 left-3 text-slateSoft pointer-events-none" />
            <input className={`${styles.input} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name" data-testid="users-search" />
          </div>
        </div>

        {!users && <Loader2 className="w-6 h-6 animate-spin text-gold-deep" />}
        {users && (
          <div className={`${styles.card} overflow-x-auto`} data-testid="users-table">
            <table className="w-full text-sm">
              <thead className="bg-cream-panel border-b border-borderSubtle text-navy/60 uppercase tracking-widest text-xs">
                <tr><th className="px-3 py-2 text-left">User</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Roles</th><th className="px-3 py-2 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u.id === me?.id;
                  return (
                    <tr key={u.id} className="border-b border-borderSubtle last:border-0 hover:bg-cream" data-testid={`user-row-${u.id}`}>
                      <td className="px-3 py-3">
                        <p className="font-semibold text-navy">{u.display_name || "—"} {isSelf && <span className="text-xs text-gold-deep">(you)</span>}</p>
                        <p className="text-xs text-slateSoft">{u.email}</p>
                      </td>
                      <td className="px-3 py-3 text-xs text-slateSoft capitalize">{(u.account_status || "").replace(/_/g, " ")}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {(u.role_assignments || []).map((a) => (
                            <span key={a.id} className={`${styles.chip} bg-navy/5 text-navy border border-navy/15`} data-testid={`role-chip-${a.role}`}>
                              {ROLE_LABEL[a.role] || a.role}{a.provider_name ? ` · ${a.provider_name}` : ""}
                              {!isSelf && (
                                <button onClick={() => revoke(u, a)} className="ml-1 text-red-600 hover:text-red-800" title="Revoke" data-testid={`role-revoke-${a.role}`}>
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </span>
                          ))}
                          {(u.role_assignments || []).length === 0 && <span className="text-xs text-slateSoft">—</span>}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right">
                        {isSelf
                          ? <span className="text-xs text-slateSoft inline-flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" /> self-managed roles locked</span>
                          : <button className={styles.btnGhost} onClick={() => setAssigning(u)} data-testid={`user-assign-${u.id}`}><Plus className="w-3.5 h-3.5" /> Assign role</button>}
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-slateSoft">No users match.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {assigning && <AssignDialog user={assigning} providers={providers} onClose={() => setAssigning(null)} onAssign={assign} />}
      </AdminShell>
    </AdminGate>
  );
}

function AssignDialog({ user, providers, onClose, onAssign }) {
  const [role, setRole] = useState("who_guide");
  const [providerId, setProviderId] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await onAssign(user.id, role, providerId);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Assignment failed.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-navy-deep/70 backdrop-blur-sm z-40 flex items-center justify-center p-6" data-testid="assign-dialog">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 lg:p-8 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="eyebrow text-gold-deep">Assign role</p>
            <h2 className="mt-1 font-display font-black text-navy text-xl">{user.email}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="assign-error">{err}</p>}
        <Field label="Role">
          <select className={styles.input} value={role} onChange={(e) => setRole(e.target.value)} data-testid="assign-role">
            {MANAGED_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </select>
        </Field>
        {role === "provider_user" && (
          <Field label="Bind to organization (required)">
            <select className={styles.input} required value={providerId} onChange={(e) => setProviderId(e.target.value)} data-testid="assign-provider">
              <option value="">— Select organization —</option>
              {providers.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
            </select>
          </Field>
        )}
        {role === "administrator" && (
          <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-center gap-2" data-testid="assign-admin-warning">
            <ShieldAlert className="w-4 h-4" /> Administrators have full control of the directory, invitations, and roles. Grant carefully — every assignment is audit-logged.
          </p>
        )}
        <div className="flex justify-end gap-3 pt-2 border-t border-borderSubtle">
          <button type="button" onClick={onClose} className={styles.btnGhost}>Cancel</button>
          <button type="submit" disabled={busy} className={styles.btnGold} data-testid="assign-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Assign role</button>
        </div>
      </form>
    </div>
  );
}
